import React, { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  PhotoIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useProvider } from '../../contexts/ProviderContext';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../lib/supabase';
import PhotoUploadModal from './PhotoUploadModal';
import ProviderLayout from './ProviderLayout';

interface ProductWithPhotoCount {
  id: string;
  sku: string;
  title: string;
  photo_url: string | null;
  vendor: string | null;
  photo_count: number;
}

type PhotoFilter = 'all' | 'with_photos' | 'without_photos';

const SkuPhotosManager: React.FC = () => {
  const { currentProviderId } = useProvider();
  const toast = useToast();

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [photoFilter, setPhotoFilter] = useState<PhotoFilter>('all');

  // Data state
  const [products, setProducts] = useState<ProductWithPhotoCount[]>([]);
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 50;

  // Modal state
  const [selectedProduct, setSelectedProduct] = useState<ProductWithPhotoCount | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Helper: attach photo counts for a list of products
  const enrichWithPhotoCounts = async (
    productsData: Array<{ id: string; sku: string; title: string; photo_url: string | null; vendor: string | null }>,
  ): Promise<ProductWithPhotoCount[]> => {
    if (!currentProviderId || productsData.length === 0) return [];

    const skus = productsData.map((p) => p.sku);
    let photoCounts: Record<string, number> = {};

    const { data: photoData, error: photoError } = await supabase
      .from('sku_photos')
      .select('sku')
      .eq('provider_id', currentProviderId)
      .in('sku', skus)
      .is('deleted_at', null);

    if (!photoError && photoData) {
      photoData.forEach((p) => {
        photoCounts[p.sku] = (photoCounts[p.sku] || 0) + 1;
      });
    }

    return productsData.map((p) => ({
      ...p,
      photo_count: photoCounts[p.sku] || 0,
    }));
  };

  // Load products with photo counts
  const loadProducts = useCallback(async () => {
    if (!currentProviderId) return;

    setLoading(true);

    try {
      // When photo filter is active, we need to know which SKUs have custom photos
      let skusWithCustomPhotos: string[] = [];
      if (photoFilter !== 'all') {
        const { data: customPhotoSkus } = await supabase
          .from('sku_photos')
          .select('sku')
          .eq('provider_id', currentProviderId)
          .is('deleted_at', null);

        if (customPhotoSkus) {
          skusWithCustomPhotos = Array.from(new Set(customPhotoSkus.map((p) => p.sku)));
        }
      }

      if (photoFilter === 'with_photos') {
        // "Has Photos" = has sku_photos OR photo_url is not null
        // Two queries to avoid broken PostgREST .or() with .in.()

        let q1 = supabase
          .from('product_descriptions')
          .select('id, sku, title, photo_url, vendor')
          .eq('provider_id', currentProviderId)
          .not('photo_url', 'is', null);

        if (searchTerm.trim()) {
          q1 = q1.or(`sku.ilike.%${searchTerm}%,title.ilike.%${searchTerm}%`);
        }

        const { data: d1 } = await q1;

        let d2Data: Array<{ id: string; sku: string; title: string; photo_url: string | null; vendor: string | null }> = [];
        if (skusWithCustomPhotos.length > 0) {
          let q2 = supabase
            .from('product_descriptions')
            .select('id, sku, title, photo_url, vendor')
            .eq('provider_id', currentProviderId)
            .in('sku', skusWithCustomPhotos);

          if (searchTerm.trim()) {
            q2 = q2.or(`sku.ilike.%${searchTerm}%,title.ilike.%${searchTerm}%`);
          }

          const { data } = await q2;
          d2Data = data || [];
        }

        // Merge and deduplicate
        const merged = new Map<string, (typeof d2Data)[number]>();
        for (const p of [...(d1 || []), ...d2Data]) {
          merged.set(p.id, p);
        }

        const allMatching = Array.from(merged.values()).sort((a, b) =>
          a.sku.localeCompare(b.sku)
        );

        const enriched = await enrichWithPhotoCounts(allMatching);

        const from = (page - 1) * itemsPerPage;
        setProducts(enriched.slice(from, from + itemsPerPage));
        setTotalCount(enriched.length);

      } else {
        // "all" or "without_photos" — server-side paginated query
        let query = supabase
          .from('product_descriptions')
          .select('id, sku, title, photo_url, vendor', { count: 'exact' })
          .eq('provider_id', currentProviderId)
          .order('sku', { ascending: true });

        if (searchTerm.trim()) {
          query = query.or(`sku.ilike.%${searchTerm}%,title.ilike.%${searchTerm}%`);
        }

        if (photoFilter === 'without_photos') {
          // "No Photos" = photo_url is null AND no custom sku_photos
          query = query.is('photo_url', null);
          if (skusWithCustomPhotos.length > 0) {
            query = query.not('sku', 'in', `(${skusWithCustomPhotos.join(',')})`);
          }
        }

        const from = (page - 1) * itemsPerPage;
        const to = from + itemsPerPage - 1;
        query = query.range(from, to);

        const { data: productsData, error: productsError, count } = await query;
        if (productsError) throw productsError;

        const combinedProducts = await enrichWithPhotoCounts(productsData || []);

        setProducts(combinedProducts);
        setTotalCount(count || 0);
      }
    } catch (err) {
      console.error('Error loading products:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [currentProviderId, searchTerm, page, photoFilter]);

  // Load products when filters change
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, photoFilter]);

  const handleProductClick = (product: ProductWithPhotoCount) => {
    setSelectedProduct(product);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedProduct(null);
  };

  const handlePhotosChanged = () => {
    // Refresh the list to update photo counts
    loadProducts();
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <ProviderLayout>
      <>
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white ">SKU PHOTOS</h1>
          <p className="mt-1 text-sm text-white/35">
            Manage custom product photos that override provider images during sync
          </p>
        </div>

        {/* Filters */}
        <div className=" rounded-lg shadow-sm border border-white/[0.08] p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/25" />
              <input
                type="text"
                placeholder="Search by SKU or product name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-white/15 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-orange-500  text-sm"
              />
            </div>

            {/* Photo Filter */}
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-5 h-5 text-white/25" />
              <select
                value={photoFilter}
                onChange={(e) => setPhotoFilter(e.target.value as PhotoFilter)}
                className="px-3 py-2 border border-white/15 rounded-lg  text-white  text-sm focus:ring-2 focus:ring-primary-500 focus:border-orange-500"
              >
                <option value="all">All Products</option>
                <option value="with_photos">Has Photos</option>
                <option value="without_photos">No Photos</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-white/35">
            {loading ? 'Loading...' : `${products.length} products`}
            {photoFilter !== 'all' && ` (filtered)`}
          </p>

          {/* Pagination Info */}
          {totalPages > 1 && (
            <p className="text-sm text-white/35">
              Page {page} of {totalPages}
            </p>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className=" rounded-lg shadow-sm border border-white/[0.08] p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-white/35  text-sm">Loading products...</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && products.length === 0 && (
          <div className=" rounded-lg shadow-sm border border-white/[0.08] p-12 text-center">
            <PhotoIcon className="w-12 h-12 text-white/15 mx-auto mb-4" />
            <p className="text-white/50 font-medium">No products found</p>
            <p className="text-white/35 text-sm mt-1">
              {searchTerm || photoFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'No products available for this provider'}
            </p>
          </div>
        )}

        {/* Product List */}
        {!loading && products.length > 0 && (
          <div className=" rounded-lg shadow-sm border border-white/[0.08] overflow-hidden">
            {/* List Header */}
            <div className="hidden sm:grid grid-cols-12 gap-4 px-4 py-3 bg-transparent border-b border-white/[0.08]  text-xs font-semibold text-white/35 uppercase">
              <div className="col-span-1 text-center">Photos</div>
              <div className="col-span-2">SKU</div>
              <div className="col-span-6">Product Name</div>
              <div className="col-span-2">Vendor</div>
              <div className="col-span-1"></div>
            </div>

            {/* List Items */}
            <div className="divide-y divide-white/[0.06]">
              {products.map((product) => (
                <div
                  key={product.id}
                  onClick={() => handleProductClick(product)}
                  className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 px-4 py-3 hover:bg-white/[0.03] cursor-pointer transition-colors"
                >
                  {/* Photo Indicator */}
                  <div className="col-span-1 flex items-center justify-center sm:justify-center">
                    {product.photo_count > 0 ? (
                      <div className="flex items-center gap-1">
                        <CheckCircleIcon className="w-5 h-5 text-green-500" />
                        <span className="text-xs  text-emerald-400 font-medium">
                          {product.photo_count}/10
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <XCircleIcon className="w-5 h-5 text-white/15" />
                        <span className="text-xs  text-white/25">
                          0/10
                        </span>
                      </div>
                    )}
                  </div>

                  {/* SKU */}
                  <div className="col-span-2 flex items-center">
                    <span className=" text-sm text-white truncate">
                      {product.sku}
                    </span>
                  </div>

                  {/* Product Name */}
                  <div className="col-span-6 flex items-center gap-3">
                    {product.photo_url ? (
                      <img
                        src={product.photo_url}
                        alt={product.title}
                        className="w-10 h-10 object-cover rounded flex-shrink-0"
                        onError={(e) => {
                          // Replace broken image with placeholder
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          target.parentElement?.querySelector('.photo-fallback')?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className={`w-10 h-10 bg-transparent rounded flex items-center justify-center flex-shrink-0 photo-fallback ${product.photo_url ? 'hidden' : ''}`}>
                      <PhotoIcon className="w-5 h-5 text-white/25" />
                    </div>
                    <span className="text-sm text-white/70 truncate">
                      {product.title}
                    </span>
                  </div>

                  {/* Vendor */}
                  <div className="col-span-2 flex items-center">
                    <span className="text-sm text-white/35 truncate">
                      {product.vendor || '-'}
                    </span>
                  </div>

                  {/* Action */}
                  <div className="col-span-1 flex items-center justify-end">
                    <button
                      className="px-3 py-1 text-xs  text-orange-400 hover:text-orange-500 hover:bg-orange-500/10 rounded transition-colors"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 bg-transparent border-t border-white/[0.08]">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="flex items-center gap-1 px-3 py-1 text-sm text-white/50 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                  Previous
                </button>

                <div className="flex items-center gap-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`px-3 py-1 text-sm  rounded ${
                          page === pageNum
                            ? 'bg-orange-600 text-white'
                            : 'text-white/50 hover:bg-white/[0.06]'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="flex items-center gap-1 px-3 py-1 text-sm text-white/50 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Photo Upload Modal */}
        {selectedProduct && currentProviderId && (
          <PhotoUploadModal
            isOpen={modalOpen}
            onClose={handleModalClose}
            product={selectedProduct}
            providerId={currentProviderId}
            onPhotosChanged={handlePhotosChanged}
          />
        )}
      </>
    </ProviderLayout>
  );
};

export default SkuPhotosManager;
